import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { RoutePaths } from 'config/route-paths';

/**
 * URL path segments for machine details drawer tabs.
 * Example: `/devices/:id/cleaning-data`, `/robot-dashboard/:id/overview`
 */
export const MACHINE_DETAILS_TAB_PATH_SEGMENTS = {
  OVERVIEW: 'overview',
  CLEANING_DATA: 'cleaning-data',
  SCHEDULE: 'schedule',
  DOCUMENTS: 'documents',
  POSITION_HISTORY: 'position-history',
} as const;

export type MachineDetailsTabKey = keyof typeof MACHINE_DETAILS_TAB_PATH_SEGMENTS;
export type MachineDetailsTabPathSegment = (typeof MACHINE_DETAILS_TAB_PATH_SEGMENTS)[MachineDetailsTabKey];

/** Host pages that can deep-link the machine details drawer via `/:id/:tab`. */
export const MACHINE_DETAILS_HOST_PATHS = [
  RoutePaths.MACHINES,
  RoutePaths.ROBOT_DASHBOARD,
  RoutePaths.MACHINE_DASHBOARD,
] as const;

export type MachineDetailsHostPath = (typeof MACHINE_DETAILS_HOST_PATHS)[number];

const TAB_KEY_BY_PATH_SEGMENT = Object.fromEntries(
  Object.entries(MACHINE_DETAILS_TAB_PATH_SEGMENTS).map(([tabKey, segment]) => [segment, tabKey])
) as Record<MachineDetailsTabPathSegment, MachineDetailsTabKey>;

export interface MachineDetailsLocationContext {
  basePath: MachineDetailsHostPath;
  machineId?: string;
  tabSegment?: string;
}

export const parseMachineDetailsLocation = (pathname: string): MachineDetailsLocationContext | undefined =>
  MACHINE_DETAILS_HOST_PATHS.reduce<MachineDetailsLocationContext | undefined>((matched, basePath) => {
    if (matched) {
      return matched;
    }
    if (pathname === basePath) {
      return { basePath };
    }
    if (pathname.startsWith(`${basePath}/`)) {
      const [machineId, tabSegment] = pathname.slice(basePath.length + 1).split('/');
      return {
        basePath,
        machineId: machineId || undefined,
        tabSegment: tabSegment || undefined,
      };
    }
    return undefined;
  }, undefined);

export const buildMachineDetailsTabPath = (
  machineId: string,
  tabKey: MachineDetailsTabKey,
  basePath: MachineDetailsHostPath = RoutePaths.MACHINES
): string => `${basePath}/${machineId}/${MACHINE_DETAILS_TAB_PATH_SEGMENTS[tabKey]}`;

export const getMachineDetailsHostBasePath = (pathname: string): MachineDetailsHostPath | undefined =>
  parseMachineDetailsLocation(pathname)?.basePath;

export interface UseMachineDetailsTabFromUrlOptions<T extends MachineDetailsTabKey> {
  validTabs: readonly T[];
  defaultTab: T;
}

/**
 * Keeps the machine details drawer tab in sync with the URL path.
 * Supports `/devices`, `/robot-dashboard`, and `/machine-dashboard` hosts.
 * - Deep-link: `/{host}/:id/overview`, `/{host}/:id/cleaning-data`, …
 * - UI tab change updates the path segment (replace)
 * - Preserves existing query string (e.g. `?editModal=true`)
 */
export const useMachineDetailsTabFromUrl = <T extends MachineDetailsTabKey>({
  validTabs,
  defaultTab,
}: UseMachineDetailsTabFromUrlOptions<T>): [T, (tab: T) => void] => {
  const location = useLocation();
  const navigate = useNavigate();
  const validTabsKey = validTabs.join('|');
  const locationContext = useMemo(() => parseMachineDetailsLocation(location.pathname), [location.pathname]);

  const isValidTab = useCallback(
    (tab: string | null | undefined): tab is T => !!tab && validTabsKey.split('|').includes(tab),
    [validTabsKey]
  );

  const tabFromUrl = useMemo((): T | undefined => {
    const segment = locationContext?.tabSegment;
    if (!segment) {
      return undefined;
    }
    const tabKey = TAB_KEY_BY_PATH_SEGMENT[segment as MachineDetailsTabPathSegment];
    return isValidTab(tabKey) ? tabKey : undefined;
  }, [isValidTab, locationContext?.tabSegment]);

  const [activeTab, setActiveTabState] = useState<T>(() => tabFromUrl ?? defaultTab);

  const updateUrlTab = useCallback(
    (tab: T) => {
      if (!locationContext?.basePath || !locationContext.machineId) {
        return;
      }

      const nextPath = buildMachineDetailsTabPath(locationContext.machineId, tab, locationContext.basePath);
      if (location.pathname === nextPath) {
        return;
      }

      navigate(
        {
          pathname: nextPath,
          search: location.search,
        },
        { replace: true }
      );
    },
    [location.pathname, location.search, locationContext, navigate]
  );

  // URL → UI
  useEffect(() => {
    if (tabFromUrl) {
      setActiveTabState(tabFromUrl);
      return;
    }
    setActiveTabState(prev => (isValidTab(prev) ? prev : defaultTab));
  }, [defaultTab, isValidTab, tabFromUrl]);

  // Ensure detail URLs carry an explicit tab segment (and rewrite invalid ones)
  useEffect(() => {
    if (!locationContext?.machineId) {
      return;
    }

    if (!locationContext.tabSegment || !tabFromUrl) {
      updateUrlTab(tabFromUrl ?? (isValidTab(activeTab) ? activeTab : defaultTab));
    }
  }, [activeTab, defaultTab, isValidTab, locationContext, tabFromUrl, updateUrlTab]);

  const setActiveTab = useCallback(
    (tab: T) => {
      setActiveTabState(tab);
      updateUrlTab(tab);
    },
    [updateUrlTab]
  );

  return [activeTab, setActiveTab];
};
